// ============================================================================
// ROLE PERMISSIONS — Constants + Helpers
// ============================================================================
// Mirrors the prima/lmms config/rolePermissions.ts pattern.
// Source of truth for permission *data* lives in admin.tbl_role_permissions
// (seed/010) and is fetched via /api/v1/RolePermissions/by-roles at login.
// This file holds:
//   1. The role-code constants the frontend pins on (so UI code uses ROLES.ADMIN
//      instead of magic strings).
//   2. Pure helpers used to merge permissions client-side (matches the merge
//      done by RolePermissionService.GetMergedByRolesAsync on the backend).

// ============================================================================
// Well-known role code constants
// ============================================================================
// These are typed constants — used by the UI where we need to reference a
// baseline role by name (e.g. locking the crs_system priority input, deciding
// whether to hide the "Delete" button on a system row). They are NOT the
// source of truth for "which roles are known" — that comes from the DB via
// getKnownRoles(). Adding a new role to the DB via the admin UI does NOT
// require adding a constant here.

export const ROLES = {
  SYSTEM:        'crs_system',
  ADMIN:         'crs_admin',
  BOOKING_AGENT: 'crs_booking_agent',
  USER:          'crs_user',
} as const;

// ============================================================================
// Actor roles + the "senior" test
// ============================================================================
// Every role the current JWT carried, as cached at login. Two screens gate on
// this (the Approval Inbox's oversight toggle and the Approval Routing editor
// in Configuration), and both must agree on what "senior" means — a drift there
// would show a control that the server then 403s, or hide one that should be
// available. So the definition lives here once.
//
// This is a CONVENIENCE gate for the UI only. Authority is always re-checked
// server-side (ApprovalController.IsSeniorActor + EnsureCanAct).

/** Lower-cased role codes from the login-time RBAC cache. Never throws. */
export function getActorRoles(): string[] {
  try {
    // Inlined key rather than importing STORAGE_KEYS: this module is loaded at
    // boot by login-interface and deliberately carries no imports (see the
    // note on the ROLE_PRIORITY block below).
    const raw = localStorage.getItem('rbac_roles');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((r) => String(r).toLowerCase()) : [];
  } catch {
    return [];
  }
}

/**
 * True for crs_admin / crs_system — the roles allowed to see oversight views
 * and edit approval routing. Fails closed on a missing/corrupt cache.
 */
export function isSeniorActor(roles: string[] = getActorRoles()): boolean {
  return roles.includes(ROLES.SYSTEM) || roles.includes(ROLES.ADMIN);
}

/**
 * Typed union of the four baseline role codes. Kept as a string alias so it
 * unifies with any custom role code from the DB.
 */
export type AppRole = (typeof ROLES)[keyof typeof ROLES] | string;

// ============================================================================
// DB-driven ROLE_PRIORITY
// ============================================================================
// ROLE_PRIORITY is no longer a compile-time constant. It's fetched from
// GET /api/v1/RolePermissions/known-roles at app boot and cached in
// localStorage under STORAGE_KEYS.KNOWN_ROLES (15-minute TTL). All resolver
// callers should use getRolePriority() rather than reading a static array.
//
// The four baseline codes below are used ONLY as a last-resort fallback for
// pre-boot code paths that must run before the fetch completes (rare —
// typically only when someone calls resolveHighestPriorityRole before
// hydrateKnownRoles has finished). In production the fallback is bypassed
// because login-interface.tsx awaits hydrateKnownRoles before calling
// resolveHighestPriorityRole.

const FALLBACK_ROLE_PRIORITY: string[] = [
  ROLES.SYSTEM,
  ROLES.ADMIN,
  ROLES.BOOKING_AGENT,
  ROLES.USER,
];

/**
 * Shape of the /known-roles response cached in localStorage.
 * Mirrors the KnownRole TS type; duplicated locally so this file has no
 * dependency on the api/types barrel (avoids a circular import at boot).
 */
interface CachedKnownRole {
  roleCode: string;
  roleName: string;
  priority: number;
  isSystemRole: boolean;
}

/**
 * 15-minute cache TTL for the known-roles catalog. Refreshed on expiry, on
 * 401 responses, or manually via `hydrateKnownRoles(force: true)`.
 */
export const KNOWN_ROLES_TTL_MS = 15 * 60 * 1000;

const KNOWN_ROLES_KEY = 'known_roles';
const KNOWN_ROLES_FETCHED_AT_KEY = 'known_roles_fetched_at';

/**
 * Returns the current known-roles catalog from localStorage, or `null` if it
 * hasn't been cached yet. Does NOT check TTL — callers who care about
 * freshness should combine this with `isKnownRolesCacheFresh()`.
 */
export function getCachedKnownRoles(): CachedKnownRole[] | null {
  try {
    const raw = localStorage.getItem(KNOWN_ROLES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed as CachedKnownRole[];
  } catch {
    return null;
  }
}

/**
 * Writes the catalog + fetched-at timestamp to localStorage. Called by
 * `hydrateKnownRoles`; also safe to call directly after a permission-write
 * operation that wants to force a refresh.
 */
export function setCachedKnownRoles(roles: CachedKnownRole[]): void {
  try {
    localStorage.setItem(KNOWN_ROLES_KEY, JSON.stringify(roles));
    localStorage.setItem(KNOWN_ROLES_FETCHED_AT_KEY, String(Date.now()));
  } catch {
    /* localStorage full / disabled — resolver will fall back to FALLBACK_ROLE_PRIORITY. */
  }
}

/**
 * True when the cached catalog was written within KNOWN_ROLES_TTL_MS.
 */
export function isKnownRolesCacheFresh(): boolean {
  try {
    const raw = localStorage.getItem(KNOWN_ROLES_FETCHED_AT_KEY);
    if (!raw) return false;
    const fetchedAt = Number.parseInt(raw, 10);
    if (!Number.isFinite(fetchedAt)) return false;
    return (Date.now() - fetchedAt) < KNOWN_ROLES_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Returns the current priority-ordered list of role codes, sourced from the
 * cached DB-driven catalog. Falls back to the four baseline codes only when
 * no cache exists (very early app boot).
 *
 * The list is ordered highest-priority-first (priority ASC), matching the
 * backend's RoleMapper contract.
 */
export function getRolePriority(): string[] {
  const cached = getCachedKnownRoles();
  if (cached && cached.length > 0) {
    return [...cached]
      .sort((a, b) => (a.priority - b.priority) || a.roleCode.localeCompare(b.roleCode))
      .map((r) => r.roleCode);
  }
  return FALLBACK_ROLE_PRIORITY;
}

/**
 * Returns the display names of all known roles, sorted priority ASC. Used by
 * login-interface.tsx to build the dynamic "please contact your administrator
 * to assign one of: X, Y, Z" error message when a user's JWT carries no CRS role.
 */
export function getKnownRoleDisplayList(): string[] {
  const cached = getCachedKnownRoles();
  if (cached && cached.length > 0) {
    return [...cached]
      .sort((a, b) => (a.priority - b.priority) || a.roleCode.localeCompare(b.roleCode))
      .map((r) => r.roleCode);
  }
  return FALLBACK_ROLE_PRIORITY;
}

/**
 * @deprecated Read the catalog dynamically via getRolePriority() instead.
 * Retained as a mutable array so any existing code that references it keeps
 * compiling; hydrateKnownRoles updates its contents on boot. New code MUST
 * call getRolePriority() to guarantee freshness.
 */
export const ROLE_PRIORITY: string[] = [...FALLBACK_ROLE_PRIORITY];

/**
 * Module ID slugs — canonical set. Must exactly match:
 *   - the `id` of every item in login-interface.tsx → navigationMenu
 *   - the keys of login-interface.tsx → modulesConfig
 *   - the entries of admin.tbl_role_permissions.visible_modules (seed/010)
 *
 * If you add a new screen to the menu, add its slug here and to seed/010.
 */
export const MODULE_IDS = {
  // Dashboard
  OVERVIEW:                    'overview',
  DASHBOARD:                   'dashboard',
  // Organisation (slice 4b)
  ORG_ENTITY:                  'org-entity',
  ORG_BUSINESS_UNIT:           'org-business-unit',
  ORG_FINANCIAL_YEAR:          'org-financial-year',
  // Property Settings
  PROPERTY_MASTER:             'property-master',
  ROOM_CATEGORY:               'room-category-master',
  ROOM_MASTER:                 'room-master',
  MEAL_PLAN:                   'meal-plan-master',
  ADDON:                       'addon-master',
  PROPERTY_TERMS:              'property-terms-master',
  BANK_ACCOUNT:                'bank-account-master',
  TAX_MASTER:                  'tax-master',
  PMS_CHARGE_TYPE:             'pms-charge-type',
  PMS_DOCUMENT_SERIES:         'pms-document-series',
  PMS_PRINT_LAYOUTS:           'pms-print-layouts',
  PMS_INHOUSE:                 'pms-inhouse',
  PMS_NIGHT_AUDIT:             'pms-night-audit',
  AUDIT_CORRECTIONS:           'audit-corrections',
  PMS_INVOICES:                'pms-invoices',
  COLLECTION_REPORT:           'collection-report',
  GUEST_LEDGER_REPORT:         'guest-ledger-report',
  WAIVER_REPORT:               'waiver-report',
  CHECKOUT_REGISTER:           'checkout-register',
  FO_CLOSING:                  'fo-closing',
  FO_COLLECTION_REPORT:        'fo-collection-report',
  MEAL_FORECAST_REPORT:        'meal-forecast-report',
  UNUSED_MEAL_REPORT:          'unused-meal-report',
  TAX_CHARGE_MAPPING:          'tax-charge-mapping',
  CONFIG_SETTINGS:             'config-settings',
  // Masters
  AGENT_MASTER:                'agent-master',
  OTA_MASTER:                  'ota-master',
  CORPORATE_MASTER:            'corporate-master',
  RATE_PLAN:                   'rate-plan-master',
  // Booking
  BOOKING_LIST:                'booking-list',
  FRONT_OFFICE:                'front-office',
  AVAILABILITY:                'availability',
  SHORTAGE_BOARD:              'shortage-board',
  GUEST_MASTER:                'guest-master',
  // Approvals
  APPROVAL_INBOX:              'approval-inbox',
  // Administration
  ROLE_PERMISSIONS:            'role-permissions',
  LEDGER_MAPPING:              'ledger-mapping',
  POSTING_REGISTER:            'posting-register',
  // Reports
  BOOKING_REGISTER_REPORT:     'booking-register-report',
  COMPLIMENTARY_REPORT:        'complimentary-report',
  DISCOUNT_REPORT:             'discount-report',
  MIN_RATE_VIOLATION_REPORT:   'min-rate-violation-report',
  MEAL_PLAN_REVENUE_REPORT:    'meal-plan-revenue-report',
  EXTRA_PAX_REPORT:            'extra-pax-report',
  FORWARD_OCCUPANCY_REPORT:    'forward-occupancy-report',
  AGENT_CORPORATE_REPORT:      'agent-corporate-report',
  DAILY_MONTHLY_SUMMARY_REPORT:'daily-monthly-summary-report',
  OCCUPANCY_CHART_REPORT:      'occupancy-chart-report',
  CHECKIN_DETAILS_REPORT:      'checkin-details-report',
  ADVANCE_PAYMENTS:            'advance-payments',
  // Mobile PWA modules (managed via the same admin.tbl_role_permissions row).
  // Kept in this file for cross-project consistency even though the mobile app
  // is a separate deploy — the DB stores WEB-* and MOB-* codes side by side.
  MOB_HOME:                    'MOB-HOME',
  MOB_GUEST:                   'MOB-GUEST',
  MOB_MEAL:                    'MOB-MEAL',
  MOB_OCC:                     'MOB-OCC',
  MOB_SHORT:                   'MOB-SHORT',
  MOB_APRV:                    'MOB-APRV',
  MOB_QBOOK:                   'MOB-QBOOK',
  MOB_MIS:                     'MOB-MIS',
} as const;

/** Action codes used as values in admin.tbl_role_permissions.module_actions. Keep in sync with seed/010. */
export const ACTIONS = {
  VIEW:           'view',
  ADD:            'add',
  EDIT:           'edit',
  DELETE:         'delete',
  SUBMIT:         'submit',
  EXPORT:         'export',
  APPROVE:        'approve',
  RESET_PASSWORD: 'reset-password',
  // TXN-STAT (Quick Status Updates — booking-list module only)
  CHECKIN:        'checkin',
  CHECKOUT:       'checkout',
  MARK_NO_SHOW:   'mark-no-show',
} as const;

// ============================================================================
// Types
// ============================================================================

export interface RolePermissionMerged {
  visibleModules: string[];
  /** Map of moduleId -> allowed action codes. */
  moduleActions: Record<string, string[]>;
  /** Role codes from the input that were actually found in admin.tbl_role_permissions. */
  sourceRoles: string[];
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Picks the highest-priority CRS role from a list of role codes (case-insensitive).
 * Returns null if none of the roles map to a CRS role.
 *
 * Mirrors RoleMapper.ResolveHighestPriorityRole on the backend.
 */
export function resolveHighestPriorityRole(roles: ReadonlyArray<string | null | undefined>): AppRole | null {
  if (!roles || roles.length === 0) return null;
  const set = new Set<string>();
  for (const r of roles) {
    if (typeof r === 'string' && r.trim().length > 0) {
      set.add(r.trim().toLowerCase());
    }
  }
  // Use the DB-driven catalog (via getRolePriority) so any role added via the
  // Role Permissions admin screen is recognised at the very next boot — no
  // code deploy required. Falls back to the four baseline codes only when the
  // cache is missing entirely (pre-boot / storage disabled).
  const priorityList = getRolePriority();
  for (const candidate of priorityList) {
    if (set.has(candidate.toLowerCase())) return candidate;
  }
  return null;
}

/**
 * Set-union of `visibleModules` across two `RolePermissionMerged` records, and
 * per-key set-union of `moduleActions`. Used to merge multiple by-roles
 * responses or to combine offline DEFAULTS with a server response.
 */
export function mergeRolePermissions(
  a: RolePermissionMerged,
  b: RolePermissionMerged,
): RolePermissionMerged {
  const modules = new Set<string>([...(a.visibleModules ?? []), ...(b.visibleModules ?? [])]);
  const actions: Record<string, string[]> = {};

  const addInto = (src: Record<string, string[]> | undefined) => {
    if (!src) return;
    for (const [moduleId, list] of Object.entries(src)) {
      const bag = new Set<string>(actions[moduleId] ?? []);
      for (const action of list ?? []) {
        if (typeof action === 'string' && action.length > 0) bag.add(action);
      }
      actions[moduleId] = Array.from(bag).sort();
    }
  };
  addInto(a.moduleActions);
  addInto(b.moduleActions);

  const roles = new Set<string>([...(a.sourceRoles ?? []), ...(b.sourceRoles ?? [])]);

  return {
    visibleModules: Array.from(modules).sort(),
    moduleActions: actions,
    sourceRoles: Array.from(roles),
  };
}

/**
 * Returns true if `merged` allows `action` on `moduleId`.
 * Cheap O(1) lookup used by PermissionGate fallbacks.
 */
export function hasAction(
  merged: RolePermissionMerged | null | undefined,
  moduleId: string,
  action: string,
): boolean {
  if (!merged) return false;
  const list = merged.moduleActions?.[moduleId];
  return Array.isArray(list) && list.includes(action);
}

/**
 * Returns true if `merged` includes the given module in `visibleModules`.
 */
export function hasModule(
  merged: RolePermissionMerged | null | undefined,
  moduleId: string,
): boolean {
  if (!merged) return false;
  return Array.isArray(merged.visibleModules) && merged.visibleModules.includes(moduleId);
}

/*
 * Permissions come exclusively from admin.tbl_role_permissions (configured
 * in seed/010 and managed via the Role Permissions screen). There is no
 * client-side fallback — if the /by-roles call returns nothing for the
 * user's role, the login flow rejects the session with a clear error
 * rather than granting any default access.
 *
 * The supreme `crs_system` role can never be locked out because the seed
 * always creates a complete row for it, and RolePermissionService rejects
 * any update that would strip its visible_modules or module_actions.
 */

// ============================================================================
// Navigation / module-config types — used by the helpers below to give them
// a strict signature without coupling to the full sidebar config types.
// ============================================================================

export interface NavMenuItem {
  id: string;
  title: string;
  icon?: string;
  active?: boolean;
  /** A group (e.g. Restaurant > Settings): its id is not a module code; its children are screens. */
  children?: NavMenuItem[];
}

export interface NavMenuSection {
  section: string;
  icon?: string;
  items: NavMenuItem[];
}

export interface ModuleConfigEntry {
  showHeader?: boolean;
  title: string;
  description?: string;
  componentName?: string;
  actions?: string[];
  permissions?: string[];
  breadcrumb?: string[];
}

export type ModulesConfigMap = Record<string, ModuleConfigEntry>;

// ============================================================================
// Menu / module-config filter helpers (mirrors prima-web/config/rolePermissions.ts)
// ============================================================================

/**
 * Returns a copy of `menu` containing only items whose `id` appears in
 * `visibleModules`. Sections that end up empty are dropped.
 *
 * Used at login time in login-interface.tsx to convert the hardcoded full
 * sidebar into a role-filtered sidebar. Mirrors prima's filterNavigationMenu.
 */
export function filterNavigationMenu(
  menu: ReadonlyArray<NavMenuSection>,
  visibleModules: ReadonlyArray<string>,
): NavMenuSection[] {
  if (!Array.isArray(menu)) return [];
  const moduleSet = new Set(visibleModules ?? []);
  // A screen shows when its module is visible; a group shows when any screen under it does.
  const keep = (items: ReadonlyArray<NavMenuItem>): NavMenuItem[] =>
    items.flatMap((item): NavMenuItem[] => {
      if (item.children && item.children.length > 0) {
        const children = keep(item.children);
        return children.length > 0 ? [{ ...item, children }] : [];
      }
      return moduleSet.has(item.id) ? [item] : [];
    });
  return menu
    .map((section): NavMenuSection => ({ ...section, items: keep(section.items ?? []) }))
    .filter((section) => section.items.length > 0);
}

/**
 * Returns a copy of `modulesConfig` keyed only by modules in `merged.visibleModules`,
 * with each entry's `actions` array overridden by `merged.moduleActions[moduleId]`.
 * Modules absent from `modulesConfig` are silently skipped.
 *
 * The `actions` array is later passed to the screen as the `allowedActions`
 * prop — screens use it to gate Add/Edit/Delete/View buttons.
 *
 * Mirrors prima's applyRoleActions.
 */
export function applyRoleActions(
  modulesConfig: ModulesConfigMap,
  merged: Pick<RolePermissionMerged, 'visibleModules' | 'moduleActions'>,
): ModulesConfigMap {
  const out: ModulesConfigMap = {};
  if (!modulesConfig || !merged) return out;
  const visible = merged.visibleModules ?? [];
  const actionsMap = merged.moduleActions ?? {};
  for (const moduleId of visible) {
    const cfg = modulesConfig[moduleId];
    if (!cfg) continue;
    out[moduleId] = {
      ...cfg,
      actions: actionsMap[moduleId] ?? [],
    };
  }
  return out;
}
